import React, {useState, useContext, useEffect} from 'react';
import '../../BidmiiCustomLoader/SmallAnimatedLoader.css';
import Dialog from '@mui/material/Dialog';
import RadioGroup from '@mui/material/RadioGroup';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import ActionButtons from './ActionButtons';
import {NewProjectContext} from '../utils/NewProjectContext';
import {useRecoilValue} from 'recoil';
import {API_URL} from '../../../constants';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import * as Sentry from '@sentry/react';
import {userState} from '../../../recoil/user/userAtom';
import BidmiiRadio from '../../../common/StyledComponents/BidmiiRadio';
import useLocationForm from '../../LocationForm/useLocationForm';
import ServiceAreaWarning from '../../NewProjectWizard/components/ServiceAreaWarning';

interface IProps {
  changeStepHandler: (number: number) => void;
  forward: boolean;
}

const ProjectAddress = ({changeStepHandler, forward}: IProps) => {
  const user = useRecoilValue(userState);
  const {project, setProject} = useContext(NewProjectContext);
  const [loading, setLoading] = useState(false);

  const getInitialAddress = () => {
    if (project?.street) {
      if (project?.street === user?.street) {
        return 'Yes';
      } else {
        return 'No';
      }
    } else {
      return null;
    }
  };

  const [useHomeAddress, setUseHomeAddress] = useState(getInitialAddress());
  const [serviceAreaWarning, setServiceAreaWarning] = useState(false);
  const [outOfProvince, setOutOfProvince] = useState(false);

  const [getAddress, locationForm, setAddressParts] = useLocationForm({
    onlyText: true,
    hideInput: useHomeAddress === 'Yes',
  });

  const toggleServiceAreaWarning = () => {
    setServiceAreaWarning(!serviceAreaWarning);
  };

  const setDefaultAddress = () => {
    if (user) {
      setAddressParts({field: 'street', payload: user.street});
      setAddressParts({field: 'city', payload: user.city});
      setAddressParts({field: 'postal', payload: user.postal});
      setAddressParts({field: 'province', payload: user.province});
    }
  };

  useEffect(() => {
    if (useHomeAddress === 'Yes') {
      setDefaultAddress();
    } else if (project.lat && project.lng) {
      setAddressParts({field: 'street', payload: project.street});
      setAddressParts({field: 'city', payload: project.city});
      setAddressParts({field: 'postal', payload: project.postal});
      setAddressParts({field: 'province', payload: project.province});
    } else {
      setAddressParts({field: 'street', payload: null});
      setAddressParts({field: 'city', payload: null});
      setAddressParts({field: 'postal', payload: null});
      setAddressParts({field: 'province', payload: null});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useHomeAddress]);

  const handleClickNext = async () => {
    const address = await getAddress();

    if (!address) {
      return;
    }
    if (
      user?.province?.substring(0, 2).toUpperCase() === 'QC' ||
      (address &&
        address?.hasOwnProperty('province') &&
        address?.province?.substring(0, 1).toUpperCase() === 'Q')
    ) {
      setOutOfProvince(true);
      toggleServiceAreaWarning();
      return;
    }

    setLoading(true);
    const rfp = {...address};

    if (localStorage.getItem('isAnonymous')) {
      rfp.enabled = false;

      sessionStorage.setItem('anonymousStreet', address?.street);
      sessionStorage.setItem('anonymousCity', address?.city);
      sessionStorage.setItem('anonymousPostal', address?.postal);
      sessionStorage.setItem('anonymousProvince', address?.province);
      sessionStorage.setItem('anonymousLng', address?.lng);
      sessionStorage.setItem('anonymousLat', address?.lat);
    }

    securedFetchRequest(`${API_URL}/rfps/${project.uid}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(rfp),
    })
      .then((res) => res.json())
      .then((res) => {
        setLoading(false);
        localStorage.setItem('rfp', JSON.stringify(res));
        setProject(res);
        changeStepHandler(1);
      })
      .catch((err) => {
        setLoading(false);
        process.env.NODE_ENV === 'development' && console.error(err);

        Sentry.withScope(function (scope) {
          scope.setUser({
            uid: user?.uid,
            firstName: user?.firstName,
          });
          scope.setTag('section', 'create_user_step_2');
          Sentry.captureException(err);
        });
      });
  };

  return (
    <>
      <Slide in={true} direction={forward ? 'left' : 'right'} timeout={400}>
        <div style={{width: '100%', maxWidth: 450}}>
          <Typography variant="h6" fontSize={22} mb={3} fontWeight="bold">
            Where is your project located?
          </Typography>
          {user ? (
            <>
              <RadioGroup
                aria-label="useHomeAddress"
                name="useHomeAddress"
                value={useHomeAddress}
                onChange={(e) => setUseHomeAddress(e.target.value)}
                style={{marginBottom: 20}}>
                <BidmiiRadio
                  state={useHomeAddress}
                  value="Yes"
                  label="My home address"
                />
                <BidmiiRadio
                  state={useHomeAddress}
                  value="No"
                  label="Other address"
                />
              </RadioGroup>
              {/* <Typography variant="caption">
              {validator.message('useHomeAddress', useHomeAddress, 'required', {
                className: 'text-danger',
              })}
            </Typography> */}
              {useHomeAddress && locationForm}
            </>
          ) : (
            <>{locationForm}</>
          )}
          <Typography
            variant="subtitle2"
            color="textSecondary"
            fontWeight={400}
            style={{marginTop: 16, maxWidth: 360}}>
            The exact address will be hidden, except to contractors with whom
            you schedule a site visit, or award the project.
          </Typography>

          <ActionButtons
            secondaryAction={() => changeStepHandler(-1)}
            primaryAction={handleClickNext}
            loading={loading}
          />
        </div>
      </Slide>

      {serviceAreaWarning && (
        <Dialog open={serviceAreaWarning} onClose={toggleServiceAreaWarning}>
          <ServiceAreaWarning
            handleSubmit={handleClickNext}
            toggleServiceAreaWarning={toggleServiceAreaWarning}
            outOfProvince={outOfProvince}
          />
        </Dialog>
      )}
    </>
  );
};

export default ProjectAddress;
